/**
 * The console's card vocabulary — three class strings, no components.
 *
 * These began in `features/billing/ui/styles.ts`, where they existed so
 * `BillingPage` and `BillingSkeleton` could not drift apart (the skeleton
 * reserves the layout the real content will occupy, so any divergence shows up
 * as a jump on the load → content swap). They live here now for the same reason
 * one level up: the platform console's screens are spread across
 * `@papan/billing`, `@papan/orgs` and `@papan/launcher`, but they are ONE
 * product to the operator looking at them, and only `@papan/core-ui` is
 * reachable from all three. A per-package copy of the recipe is how two cards
 * on one screen end up with different corner radii.
 *
 * `features/billing/ui/styles.ts` re-exports these, so its own import sites are
 * unchanged.
 */

/**
 * Card surface for every block on a console page: a rounded, softly-ringed
 * glass panel.
 *
 * The "command center" redesign gives these more presence than the old flat
 * rounded-lg fills — larger corner radius, a hairline ring, and a whisper of
 * shadow so each band reads as its own object without floating off the page.
 * It is written with the theme CSS variables (--bg-secondary / --border-
 * primary) rather than Tailwind palette classes so the same recipe renders as
 * a frosted-dark or frosted-light card across all three themes; the accent and
 * glow (violet) live on the individual elements that need to pop, not here.
 *
 * Still deliberately NOT .ws-card / .ws-card-hero — those are tuned for cards
 * floating over the workspace stage and read as heavy inside a settings pane.
 */
export const CARD =
  'rounded-2xl bg-[color:var(--bg-secondary)] ring-1 ring-[color:var(--border-primary)] shadow-[0_1px_3px_rgba(0,0,0,0.06)]'

/**
 * Section eyebrow. Small, upper-case and letter-spaced rather than another
 * heading size — it separates the page into scannable bands without competing
 * with the figures inside each card, which are what should carry the weight.
 */
export const SECTION_LABEL =
  'text-[11px] font-semibold uppercase tracking-[0.08em] themed-text-muted'

/**
 * Money, counts and dates use lining/tabular figures so columns align and a
 * changing value doesn't reflow the text around it.
 */
export const NUMERIC = 'tabular-nums'
